"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui";
import { FlamingoMark } from "@/components/brand/FlamingoMark";

const STORAGE_KEY = "pf_age_ok";

/**
 * Full-screen 18+ confirmation shown on first visit. Rendered as an overlay so
 * the underlying page content stays in the DOM (crawlers are not blocked). The
 * choice is remembered in localStorage; "Leave" redirects away.
 */
export function AgeGate() {
  const t = useTranslations("ageGate");
  // Start hidden to avoid a flash; reveal only if not previously confirmed.
  // `ready` guards the SSR/first paint where localStorage isn't available.
  const [state, setState] = useState({ ready: false, open: false });
  const { ready, open } = state;

  useEffect(() => {
    let confirmed = false;
    try {
      confirmed = localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      confirmed = false;
    }
    // Gate visibility depends on client-only localStorage (absent during SSR),
    // so it can only be resolved here after mount.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setState({ ready: true, open: !confirmed });
  }, []);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (open) document.getElementById("age-gate-enter")?.focus();
  }, [open]);

  function enter() {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* ignore storage failures — the gate simply reappears next visit */
    }
    setState({ ready: true, open: false });
  }

  function leave() {
    window.location.href = "https://www.google.com";
  }

  if (!ready || !open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="age-gate-title"
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-ink/85 px-6 py-10 backdrop-blur-2xl"
    >
      <div className="animate-scale-in flex w-full max-w-md flex-col items-center text-center">
        <div className="mb-6 flex size-20 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04] shadow-glow-sm">
          <FlamingoMark className="w-auto" style={{ height: "3rem" }} />
        </div>

        <p className="mb-2 text-xs font-semibold tracking-[0.2em] text-flamingo/80 uppercase">
          {t("eyebrow")}
        </p>
        <h1
          id="age-gate-title"
          className="font-display text-3xl font-semibold text-white sm:text-4xl"
        >
          {t("title")}
        </h1>
        <p className="mt-4 max-w-sm leading-relaxed text-white/60">
          {t("body")}
        </p>

        <div className="mt-8 flex w-full flex-col gap-3 sm:flex-row sm:justify-center">
          <Button id="age-gate-enter" size="lg" onClick={enter} className="sm:min-w-40">
            {t("enter")}
          </Button>
          <Button size="lg" variant="ghost" onClick={leave} className="sm:min-w-40">
            {t("leave")}
          </Button>
        </div>

        <p className="mt-6 text-xs text-white/50">
          {t("deviceNote")}
        </p>
      </div>
    </div>
  );
}
